import { useState } from 'react'
import { SuggestionKind, api } from '../lib/api'
import { nameToShare, rememberSharing, sharingPreferred } from '../lib/learnedNames'
import { categoryAfterNameChange, categoryOptions, namesFor, useNameSuggestions } from '../lib/suggestions'

/**
 * The name and category of an item, with suggestions for both (SM-21), for
 * a form to show with two Comboboxes. Anything can still be typed.
 *
 * Also whether to share the name when it's saved (SM-22): `share` for the
 * form's checkbox, and `shareOnSave` to call as it saves.
 */
export function useSuggestedNameAndCategory(kind: SuggestionKind, initialName: string, initialCategory: string) {
  const all = useNameSuggestions()
  const [name, setNameOnly] = useState(initialName)
  const [category, setCategoryOnly] = useState(initialCategory)
  // The category the last picked suggestion filled in, so the next pick may replace it.
  const [filledIn, setFilledIn] = useState<string | null>(null)
  const [share, setShare] = useState(sharingPreferred)

  function setName(next: string) {
    setNameOnly(next)
    const after = categoryAfterNameChange(all, kind, next, category, filledIn)
    setCategoryOnly(after.category)
    setFilledIn(after.filledIn)
  }

  /** Remembers the choice, and shares the name if it's new — unless it's unchanged from before the edit. */
  function shareOnSave() {
    rememberSharing(share)
    if (!share || name.trim() === initialName.trim()) return
    const shared = nameToShare(all, kind, name, category)
    // keepalive: the form closes as it saves. A share that fails is simply not counted.
    if (shared) api.shareName(shared).catch(() => {})
  }

  return {
    name,
    setName,
    category,
    setCategory: setCategoryOnly,
    nameOptions: namesFor(all, kind).map((s) => s.name),
    categoryOptions: categoryOptions(all),
    share,
    setShare,
    shareOnSave,
  }
}
